import React, { useEffect } from 'react'
import { Box, Button, Divider, Tooltip, Typography } from '@mui/material'
import { Clear as ClearIcon } from '@mui/icons-material'
import CircularProgress from '@mui/material/CircularProgress'
import { useDispatch, useSelector } from 'react-redux'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import { DataGrid } from '@mui/x-data-grid'
import { useStyles } from '../Pathologist/inboxStyles'
import { fetchCaseHistory } from '../../../actions'
import { RootState } from '../../../reducers'
import { SlideStatus, CaseStatus } from '../../../utils/displayLabels'
import { format, parseISO } from 'date-fns'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'

const PatientCaseHistory = ({ userDetails, handleClose }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { historyData, fetchHistoryInProgress } = useSelector((state: RootState) => state.getCaseHistory)
  const { display_labels } = useSelector((state: RootState) => state.appConfig)
  const { role } = useSelector((state: RootState) => state.userLogin)
  const procedureData = useSelector((state: RootState) => state.getFilterProcedureCodeData)

  const historyColumns = [
    {
      field: 'created_datetime',
      headerName: 'Updated at',
      headerClassName: 'super-app-theme--header',
      width: 180,
      valueGetter: (param) => `${format(parseISO(param.row.created_datetime + ''), 'yyyy-MM-dd HH:mm:ss')}`,
    },
    {
      field: 'user_id',
      headerName: 'Pathologist',
      headerClassName: 'super-app-theme--header',
      width: 155,
    },
    {
      field: 'created_by',
      headerName: 'Updated by',
      headerClassName: 'super-app-theme--header',
      width: 155,
    },
    {
      field: 'workflow_stage_id',
      headerName: 'Case status',
      headerClassName: 'super-app-theme--header',
      width: 200,
      renderCell: (param) => {
        const caseStatus = CaseStatus(param.row?.stage_id, role, display_labels)
        const statusArray = caseStatus.labelStr.split(', ')
        const style = { backgroundColor: caseStatus.labelColor }
        return (
          <>
            {statusArray.map((caseStatus) => (
              // eslint-disable-next-line react/jsx-key
              <>
                <Tooltip title={caseStatus} placement="top-start">
                  <Stack direction="row" spacing={2}>
                    <Chip className={classes.chipSpacing} color="primary" style={style} label={caseStatus} />
                  </Stack>
                </Tooltip>
              </>
            ))}
          </>
        )
      },
    },
    {
      field: 'slide_status_id',
      headerName: 'Slide status',
      headerClassName: 'super-app-theme--header',
      width: 210,
      renderCell: (param) => {
        const value = SlideStatus(param.row?.slide_status_id, role, display_labels)
        const truncatedValue = value.length >= 30 ? value.substring(0, 30) + '...' : value
        return (
          <Tooltip title={value} placement="top-start">
            <div>{truncatedValue}</div>
          </Tooltip>
        )
      },
    },
  ]

  const filterUserResults = procedureData.procedureCodeMap.filter((value) => {
    return value.id == userDetails?.procedure_code
  })

  useEffect(() => {
    if (userDetails?.case_id) {
      dispatch(fetchCaseHistory(userDetails?.case_id, role))
    }
  }, [dispatch, userDetails])

  return (
    <Box className={`${classes.mt} , ${classes.mb}`}>
      <Dialog
        open={true}
        onClose={handleClose}
        maxWidth={'lg'}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        className={classes.dialogHeight}
      >
        <DialogTitle className={classes.dialogTitle}>Case History</DialogTitle>
        <Divider />
        <DialogContent sx={{ minWidth: '950px', minHeight: '600px' }}>
          <TableContainer component={Paper}>
            <Table className={classes.table} aria-label="simple table">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#0d688d73', fontWeight: '600' }}>
                  <TableCell>TRF ID</TableCell>
                  <TableCell>Created On</TableCell>
                  <TableCell>Patient Name</TableCell>
                  <TableCell>Gender</TableCell>
                  <TableCell>Case Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody className={classes.tableBody}>
                <TableRow key={userDetails?.case_id}>
                  <TableCell component="th" scope="row">
                    {userDetails?.case_number}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {userDetails?.created_datetime}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {userDetails?.patient_last_name}, {userDetails?.patient_first_name}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {userDetails?.patient_gender}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {filterUserResults[0]?.display}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <div className={classes.history}>
            <Typography variant="h6" component="h6">
              Case workflow log
              <hr className={classes.hr} />
            </Typography>
            {historyData?.data?.case_history ? (
              <Box
                sx={{
                  height: '400px',
                  width: '100%',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#0d688d73',
                    fontWeight: '600',
                  },
                  '& .MuiDataGrid-columnHeader--moving': {
                    backgroundColor: 'transparent !important',
                  },
                }}
              >
                <DataGrid
                  disableColumnSelector
                  disableExtendRowFullWidth
                  rows={historyData?.data?.case_history}
                  columns={historyColumns}
                  loading={fetchHistoryInProgress}
                  hideFooter={true}
                />
              </Box>
            ) : (
              <Box>
                <Typography
                  color="disabled"
                  sx={{ fontSize: '20px', fontWeight: 500, m: '50px 0px 63px 300px', color: 'lightgrey' }}
                  variant="h2"
                >
                  <CircularProgress />
                </Typography>
              </Box>
            )}
          </div>
        </DialogContent>
        <DialogActions className={classes.buttonActions}>
          <Button onClick={handleClose} variant="outlined" endIcon={<ClearIcon />}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default React.memo(PatientCaseHistory)
